import React, { useRef, useEffect, useState } from 'react'
import {
  FileManagerComponent,
  Inject,
  NavigationPane,
  DetailsView,
  Toolbar,
  ToolbarItemsDirective,
  ToolbarItemDirective
} from '@syncfusion/ej2-react-filemanager'

import {
  DialogDetailsPure,
  DialogTwoActions,
  DialogYesNoHLPure,
  DropDownBtnHLPure,
  InputHLPure,
  DialogExtractEntities
} from '../../pures'
import './syncfusion-file-manager.pure.css'
import { BusinessAdministrationValidator } from '../../../validators'
import { useDeviceType } from '../../../hooks'

/**
 * @author Edgar Escobedo
 * @created 02-09-2025
 * @description - Dialog to display a list of details.
 * @typedef {Object} Props
 * @property {Array<{ detail: string, value: string | number, line:boolean | null }> } details - Array of objects with 'detail' and 'value' keys.
 * @param {Props}
 * @returns {JSX.Element}
 */
const ChildrenDialogDetails = ({ details }) => {
  return (
    <div className='syncfusionFileManager-dialog-details'>
      <ul>
        {details.map((detail, index) => {
          if (detail.line) {
            return <hr key={index} className='syncfusionFileManager-hr' />
          } else {
            return (
              <li key={index}>
                <span className='syncfusionFileManager-labelKey'>{detail.detail}:</span>{' '}
                <span className='syncfusionFileManager-labelValue'>{detail.value}</span>
              </li>
            )
          }
        })}
      </ul>
    </div>
  )
}

/**
 * @author Marissa Vargas
 * @description - Custom implementation of Syncfusion's File Manager component - To display categories, subcategories and files by company in Digital Vault.
 * @typedef {Object} Props
 * @property {function} addNewFile - Function that redirects to the digital vault's upload page.
 * @property {Array} categoriesData - Array of categories.
 * @property {function} checkDepth - Custom function to check the depth of a folder.
 * @property {Array<{ value: string, name: string }>} companies Array of companies to display in the popup dropdown.
 * @property {string} companyId - Selected company id.
 * @property {function} createNewCategory - Function to create a new category.
 * @property {function} deleteFiles - Function to delete categories, subcategories or files.
 * @property {Array} documentsData - Array of documents.
 * @property {function} downloadFile - Function to download folders or files.
 * @property {string} fileManagerPath - String with the directory path to open the fileToChange manager.
 * @property {Array<{ dateCreated: string, dateModified: string, filterPath: string, hasChild: boolean, id: number|string, imageUrl: string, isFile: boolean, name: string, parentId: number | string, permission: Object, size: number, type: string }>} fileSystemData - Array of data to populate folders/files in the File Manager.
 * @property {Object} fileObj - Reference to the file manager's instance.
 * @property {boolean} isLoading - Boolean state if the backend request is
 * @property {string} language - Language selected by the user.
 * @property {function} moveFiles - Function to move folders or files.
 * @property {Object<{ category: { copy: boolean, create: boolean, delete: boolean, download: boolean, rename: boolean }, subcategory: { copy: boolean, create: boolean, delete: boolean, download: boolean, rename: boolean }}>} permission - Object with action permissions. Varies per user.
 * @property {function} onFileOpen - Custom function triggered when a fileToChange/folder is opened.
 * @property {function} onFileSelect - Custom function triggered when a fileToChange/folder is selected.
 * @property {function} onMenuOpen - Custom function triggered when a context menu is opened.
 * @property {function} refreshFileManager - Function to trigger fetch of file manager's data.
 * @property {function} renameFile - Function to rename a folder or file.
 * @property {function} setCompanyId - Function to change the company id.
 * @property {function} setFirstLoad - Function to update the first load state.
 * @property {function} setIsLoading - Function to update loading state.
 * @property {function} setRefresh - Function to fetch data again and refresh the component.
 * @property {boolean} showAddFileBtn - Boolean state to show/hide the add fileToChange button in the toolbar.
 * @property {function} sortComparer - Custom function to sort by date.
 * @property {string} strBtn - String that appears in the new folder button.
 * @property {function} t - Translation function.
 * @property {Array<{}>}
 * @property {function} toggleErrorRequesting - Function to activate the error dialog.
 * @property {boolean} showAnalyzeBtn - Boolean state to show/hide the analyze button in the toolbar.
 * @property {boolean} showModalEntities - Boolean state to show/hide the modal to extract entities.
 * @property {function} handleShowModalEntities - Function to open the modal to extract entities.
 * @property {function} closeModalEntities - Function to close the modal to extract entities.
 * @property {function} saveExtractedEntities - Function to save the extracted entities.
 * @property {function} onClickCheckBox - Function to handle checkbox click events for documents to OCR.
 * @property {Array} documentsToOCR - Array of documents selected for OCR processing.
 * @property {number} totalCredits - Total credits available for OCR processing.
 * @param {Props} props
 * @returns {JSX.element}
 */
function SyncfusionFileManager({
  addNewFile,
  categoriesData,
  checkDepth,
  checkPermission,
  companies,
  companyId,
  copyFiles,
  createNewCategory,
  deleteFiles,
  documentsData,
  downloadFile,
  fileManagerPath,
  fileObj,
  fileSystemData,
  language,
  moveFiles,
  permission,
  onCreated,
  onFileOpen,
  onFileSelect,
  onMenuOpen,
  refreshFileManager,
  renameFile,
  setCompanyId,
  setIsLoading,
  setFirstLoad,
  showAddFileBtn,
  sortComparer,
  strBtn,
  t,
  userLevelOptions,
  toggleErrorRequesting,
  showAnalyzeBtn,
  showModalEntities,
  handleShowModalEntities,
  closeModalEntities,
  saveExtractedEntities,
  onClickCheckBox,
  documentsToOCR,
  totalCredits
}) {
  const validator = BusinessAdministrationValidator(t)

  const addFileBtn = useRef(null)
  const analyzeFileBtn = useRef(null)
  const lastSelectedFileRef = useRef(null)

  const [showPopup, setShowPopup] = useState(false)
  const [showAnalyzeFileButton, setShowAnalyzeFileButton] = useState(false)

  const [showCategoryDialog, setShowCategoryDialog] = useState(false)
  const [showCategoryInputDialog, setShowCategoryInputDialog] = useState(false)
  const [categoryName, setCategoryName] = useState('')
  const [level, setLevel] = useState(1)
  const [parentId, setParentId] = useState(0)
  const [categoryNameError, setCategoryNameError] = useState(false)

  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [detailsData, setDetailsData] = useState([])

  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [newFileName, setNewFileName] = useState('')
  const [levelUser, setLevelUser] = useState('')
  const [fileType, setFileType] = useState('')
  const [fileToChange, setFileToChange] = useState({})

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [filesToDelete, setFilesToDelete] = useState([])

  const { isMobile } = useDeviceType()

  const keyLabelsMap = {
    company: [
      { key: 'name', label: t('fileManager.detailsDialog.labels.companyName') },
      {
        key: 'businessName',
        label: t('fileManager.detailsDialog.labels.businessName'),
      },
      {
        line: true
      },
      {
        key: 'totalDocuments',
        label: t('fileManager.detailsDialog.labels.totalDocuments'),
      },
      {
        key: 'totalExpedientes',
        label: t('fileManager.detailsDialog.labels.totalExpedientes'),
      },
      {
        line: true
      },
      {
        key: 'totalNotificationExpired',
        label: t('fileManager.detailsDialog.labels.totalNotificationExpired'),
      },
      {
        key: 'totalExpired',
        label: t('fileManager.detailsDialog.labels.totalExpired'),
      },
      { key: 'relation', label: t('fileManager.detailsDialog.labels.relation') },
      {
        line: true
      }
    ],
    category: [
      {
        key: 'name',
        label: t('fileManager.detailsDialog.labels.categoryName'),
      },
      {
        line: true
      },
      {
        key: 'totalDocuments',
        label: t('fileManager.detailsDialog.labels.totalDocuments'),
      },
      {
        key: 'totalExpedientes',
        label: t('fileManager.detailsDialog.labels.totalExpedientes'),
      },
      {
        line: true
      },
      {
        key: 'totalNotificationExpired',
        label: t('fileManager.detailsDialog.labels.totalNotificationExpired'),
      },
      {
        key: 'totalExpired',
        label: t('fileManager.detailsDialog.labels.totalExpired'),
      },
      { key: 'relation', label: t('fileManager.detailsDialog.labels.relation') },
      {
        line: true
      }
    ],
    document: [
      {
        key: 'name',
        label: t('fileManager.detailsDialog.labels.documentName'),
      },
      {
        key: 'userName',
        label: t('fileManager.detailsDialog.labels.owner'),
      },
      {
        line: true
      },
      {
        key: 'createDateStandardComplete',
        label: t('fileManager.detailsDialog.labels.startDate'),
      },
      {
        key: 'validationDateStandardComplete',
        label: t('fileManager.detailsDialog.labels.expirationDate'),
      },
      {
        line: true
      },
      {
        key: 'typeDocumentName',
        label: t('fileManager.detailsDialog.labels.typeDocument'),
      },
      {
        key: 'confidential',
        label: t('fileManager.detailsDialog.labels.confidential'),
      },
      {
        line: true
      },
      {
        key: 'description',
        label: t('fileManager.detailsDialog.labels.description'),
      },
      {
        key: 'summary',
        label: t('fileManager.detailsDialog.labels.summary.ia'),
      },
      {
        line: true
      }
    ],
    expediente: [
      { key: 'name', label: t('fileManager.detailsDialog.labels.fileName') },
      {
        key: 'updatedDateStandardComplete',
        label: t('fileManager.detailsDialog.labels.updatedDate'),
      },
      {
        line: true
      },
      {
        key: 'totalFiles',
        label: t('fileManager.detailsDialog.labels.filesIncluded'),
      },
      {
        key: 'userName',
        label: t('fileManager.detailsDialog.labels.owner'),
      },
      {
        key: 'confidential',
        label: t('fileManager.detailsDialog.labels.confidential'),
      },
      {
        line: true
      },
      {
        key: 'expired',
        label: t('fileManager.detailsDialog.labels.expired'),
      },
      {
        key: 'relation',
        label: t('fileManager.detailsDialog.labels.relation'),
      },
      {
        line: true
      }
    ],
  }

  const childrenDialog = <ChildrenDialogDetails details={detailsData} />

  function getCategoryDialogText(key) {
    const isCategory = strBtn === t('fileManager.toolbar.newCategory')
    const map = {
      saveBtn: isCategory
        ? t('fileManager.createCategory.dialog.saveBtn')
        : t('fileManager.createSubcategory.dialog.saveBtn'),
      cancelBtn: t('fileManager.createCategory.dialog.cancelBtn'),
      header: isCategory
        ? t('fileManager.createCategory.dialog.header')
        : t('fileManager.createSubcategory.dialog.header'),
      errorInputText: isCategory
        ? t('fileManager.createCategory.dialog.errorInputText')
        : t('fileManager.createSubcategory.dialog.errorInputText'),
      categoryInput: isCategory
        ? t('fileManager.createCategory.dialog.categoryInput')
        : t('fileManager.createSubcategory.dialog.categoryInput'),
      confirmHeader: isCategory
        ? t('fileManager.createCategory.dialog.confirmHeader')
        : t('fileManager.createSubcategory.dialog.confirmHeader'),
      confirmDescription: isCategory
        ? t('fileManager.createCategory.dialog.confirmDescription')
        : t('fileManager.createSubcategory.dialog.confirmDescription'),
    }
    return map[key]
  }

  /** Use of MutationObserver to keep click attached to main node of tree view */
  useEffect(() => {
    let currentHeader = null

    const handleClick = (e) => {
      setShowPopup((prev) => !prev)
    }

    const attachListener = () => {
      const header = document.querySelector('.e-fullrow')
      if (header && header !== currentHeader) {
        if (currentHeader) {
          currentHeader.removeEventListener('click', handleClick)
        }
        header.addEventListener('click', handleClick)
        currentHeader = header
      }
    }

    // Initial attach
    attachListener()

    // Observe DOM changes to re-attach if .e-fullrow is replaced
    const observer = new MutationObserver(() => {
      attachListener()
    })

    const container = document.getElementById(
      'DigitalVault_filemanager_container'
    )
    if (container) {
      observer.observe(container, { childList: true, subtree: true })
    }

    return () => {
      if (currentHeader) {
        currentHeader.removeEventListener('click', handleClick)
      }
      observer.disconnect()
    }
  }, [])


  useEffect(() => {
    if (!lastSelectedFileRef.current) return
    const multiple = fileObj && fileObj.current && Array.isArray(fileObj.current.selectedItems) && fileObj.current.selectedItems.length > 1
    if (multiple) {
      setShowAnalyzeFileButton(false)
      return
    }

    const updatedDoc = documentsData && documentsData.find && documentsData.find(d => d.id === lastSelectedFileRef.current.id)
    if (!updatedDoc) {
      setShowAnalyzeFileButton(false)
      lastSelectedFileRef.current = null
      return
    }

    const statusValue = updatedDoc.statusIA || updatedDoc.StatusAi || updatedDoc.statusAi
    const notAnalyzedText = t('fileManager.listView.header.status.ia.no.analized')
    const canAnalyze = statusValue === notAnalyzedText
    setShowAnalyzeFileButton(!!canAnalyze)
    if (!canAnalyze) {
      lastSelectedFileRef.current = null
    }
  }, [documentsData, showModalEntities, t, fileObj])

  function getDetails({ obj, type }) {
    const keyLabels = keyLabelsMap[type]
    const data = keyLabels.map(({ key, label, line }) => {
      let value = obj[key]
      if (label === t('fileManager.detailsDialog.labels.confidential')) value = obj[key] ? t('fileManager.detailsDialog.labels.confidential.yes') : t('fileManager.detailsDialog.labels.confidential.no')
      const data = {
        detail: label,
        value,
        line
      }
      return data
    })

    return data
  }

  function getRelation({ obj }) {
    if (obj.totalExpired === 0) {
      return 0
    } else {
      return Math.ceil(
        Number((obj.totalDocuments + obj.totalExpedientes) / obj.totalExpired)
      )
    }
  }

  function getItemDetails(item) {
    if (item.type === 'folder') {
      const depth = checkDepth({ str: item.filterPath })
      const obj = {
        ...categoriesData[0],
        relation: getRelation({ obj: categoriesData[0] }),
      }

      if (depth === 1) {
        return getDetails({ obj: obj, type: 'company' })
      } else {
        const matched = categoriesData.find((el) => el.id === item.id)
        const obj = {
          ...matched,
          relation: getRelation({ obj: matched }),
        }

        return getDetails({ obj: obj, type: 'category' })
      }
    } else if (item.isFile) {
      const matched = documentsData.find((el) => el.id === item.id)
      if (!matched) return []
      const rawInfoType = (matched.infoType || '').toString().toLowerCase()
      const isDocument = ['documento', 'document', 'doc'].some((v) => rawInfoType.startsWith(v))
      const isExpediente = ['expediente', 'record', 'file', 'folderfile'].some((v) => rawInfoType.startsWith(v))

      if (isDocument) {
        return getDetails({ obj: matched, type: 'document' })
      }
      if (isExpediente) {
        return getDetails({ obj: matched, type: 'expediente' })
      }
      return getDetails({ obj: matched, type: 'document' })
    }
    return []
  }

  const onFileLoad = (args) => {
    if (
      !args.fileDetails.isFile &&
      (args.module === 'LargeIconsView' || args.module === 'DetailsView')
    ) {
      const element = args.element
      const depthLevel =
        checkDepth({ str: args.fileDetails.filterPath })
      element.classList.add(`custom-folder-level-${depthLevel}`)
    }
  }

  const handleFileSelectInternal = (args) => {
    try {
      const { fileDetails, selectedItems } = args || {}
      let list = Array.isArray(fileDetails) ? fileDetails : (fileDetails ? [fileDetails] : [])

      const multipleImmediate = fileObj && fileObj.current && Array.isArray(fileObj.current.selectedItems) && fileObj.current.selectedItems.length > 1
      if (multipleImmediate) {
        setShowAnalyzeFileButton(false)
        lastSelectedFileRef.current = null
        if (onFileSelect) onFileSelect(args)
        return
      }

      if (args && args.action === 'unselect') {
        const remaining = (selectedItems && Array.isArray(selectedItems))
          ? selectedItems.length
          : (fileObj && fileObj.current && fileObj.current.selectedItems
            ? fileObj.current.selectedItems.length
            : list.length)
        if (remaining === 0) {
          setShowAnalyzeFileButton(false)
          lastSelectedFileRef.current = null
          if (onFileSelect) onFileSelect(args)
          return
        }
      }

      if (selectedItems && Array.isArray(selectedItems) && selectedItems.length !== list.length) {
        if (selectedItems.length !== 1) {
          setShowAnalyzeFileButton(false)
          lastSelectedFileRef.current = null
          if (onFileSelect) onFileSelect(args)
          return
        }
      }

      if (list.length !== 1 || !list[0].isFile) {
        setShowAnalyzeFileButton(false)
        lastSelectedFileRef.current = null
      } else {
        const fd = list[0]
        const statusValue = fd.statusIA || fd.StatusAi || fd.statusAi
        const notAnalyzedText = t('fileManager.listView.header.status.ia.no.analized')
        const canAnalyze = statusValue === notAnalyzedText
        setShowAnalyzeFileButton(!!canAnalyze)
        lastSelectedFileRef.current = fd
        if (!canAnalyze) {
          lastSelectedFileRef.current = null
        }
      }

      setTimeout(() => {
        const multiplePost = fileObj && fileObj.current && Array.isArray(fileObj.current.selectedItems) && fileObj.current.selectedItems.length > 1
        if (multiplePost) {
          setShowAnalyzeFileButton(false)
        }
      }, 0)
    } catch (err) {
      setShowAnalyzeFileButton(false)
    }
    if (onFileSelect) onFileSelect(args)
  }

  function getNewCategoryParams(item) {
    if (item.type === 'folder') {
      const depth = checkDepth({ str: item.filterPath })
      setLevel(depth)
      setParentId(item.id)
    }
  }

  function getFileType(item) {
    if (item.isFile) {
      setFileType(t('fileManager.renameFile.dialog.file'))
    } else {
      const depth = checkDepth({ str: item.filterPath })

      if (depth === 2) {
        setFileType(t('fileManager.renameFile.dialog.cat'))
      } else {
        setFileType(t('fileManager.renameFile.dialog.sub'))
      }
    }
  }

  function toolbarClickHandler(args) {
    const elementId = fileObj.current.element.id
    const { fileDetails } = args

    switch (args.item.id) {
      case `${elementId}_tb_newfolder`:
        if (strBtn === t('fileManager.toolbar.newCategory')) {
          if (!permission.category.create) {
            toggleErrorRequesting(t('fileManager.permission.category.create'))
            return
          }
        } else {
          if (!permission.subcategory.create) {
            toggleErrorRequesting(t('fileManager.permission.subcategory.create'))
            return
          }
        }
        setCategoryName('')
        setCategoryNameError(false)
        setShowCategoryInputDialog(true)
        getNewCategoryParams(fileDetails[0])
        break
      case `${elementId}_tb_refresh`:
        refreshFileManager(true)
        break
      case `${elementId}_tb_rename`:
        if (
          !checkPermission({
            action: 'rename',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        getFileType(fileDetails[0])
        setShowRenameDialog(true)
        setFileToChange(fileDetails[0])
        setNewFileName(fileDetails[0].name)
        setLevelUser(fileDetails[0].levelUser)
        break
      case `${elementId}_tb_delete`:
        if (
          !checkPermission({
            action: 'delete',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        setShowDeleteDialog(true)
        setFilesToDelete(fileDetails)
        break
      case `${elementId}_tb_details`:
        if (fileDetails.length === 0) {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails({
            type: 'folder',
            filterPath: ''
          }))
        } else {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails(fileDetails[0]))
        }
        break
      default:
        break
    }
  }

  function menuClick(args) {
    const elementId = fileObj.current.element.id
    const { fileDetails } = args

    switch (args.item.id) {
      case `${elementId}_cm_newfolder`:
        if (strBtn === t('fileManager.toolbar.newCategory')) {
          if (!permission.category.create) {
            toggleErrorRequesting(t('fileManager.permission.category.create'))
            return
          }
        } else {
          if (!permission.subcategory.create) {
            toggleErrorRequesting(t('fileManager.permission.subcategory.create'))
            return
          }
        }
        setCategoryName('')
        setCategoryNameError(false)
        setShowCategoryInputDialog(true)
        getNewCategoryParams(fileDetails[0])
        break
      case `${elementId}_cm_details`:
        if (fileDetails.length === 0) {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails({
            type: 'folder',
            filterPath: ''
          }))
        } else {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails(fileDetails[0]))
        }
        break
      case `${elementId}_cm_rename`:
        if (
          !checkPermission({
            action: 'rename',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        getFileType(fileDetails[0])
        setShowRenameDialog(true)
        setFileToChange(fileDetails[0])
        setNewFileName(fileDetails[0].name)
        setLevelUser(fileDetails[0].levelUser)
        break
      case `${elementId}_cm_delete`:
        if (
          !checkPermission({
            action: 'delete',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        setShowDeleteDialog(true)
        setFilesToDelete(fileDetails)
        break
      case `${elementId}_cm_refresh`:
        refreshFileManager(true)
        break
      case `${elementId}_cm_analyze`:
        fileObj.current.documentSelected = (fileDetails && fileDetails.length > 0) ? fileDetails[0] : null
        handleShowModalEntities()
        break
      default:
        break
    }
  }

  function validateNameInput(name) {
    const MAX_NAME_LENGTH = 128
    if (name.length > MAX_NAME_LENGTH) {
      return false
    }

    const validationError = validator.businessNameValidator(name)
    return validationError === ''
  }

  function handleSubmitCategoryInput() {
    if (!validateNameInput(categoryName)) {
      setCategoryNameError(true)
      return
    }
    setCategoryNameError(false)
    setShowCategoryInputDialog(false)
    setShowCategoryDialog(true)
  }

  function handleSubmitRenameInput() {
    if (!validateNameInput(newFileName)) {
      setCategoryNameError(true)
      return
    }
    setCategoryNameError(false)

    const level = checkDepth({ str: fileToChange.filterPath }) - 1

    renameFile({
      file: {
        ...fileToChange,
        newName: newFileName.trim(),
        level
      },
      levelUser
    })

    setShowRenameDialog(false)
    setNewFileName('')
  }

  function handleCreateNewCategory({ allCompanies }) {
    createNewCategory({
      categoryName: categoryName.trim(),
      companyId: companyId,
      createAllCompanies: allCompanies,
      level: level,
      parent: parentId === 0 ? null : parentId,
      type: level === 1 ? 'Cat' : 'Sub',
    })

    setShowCategoryDialog(false)
    setCategoryName('')
    setParentId(0)
    setLevel(1)
  }

  function handleCreateNewSubcategory() {
    if (!validateNameInput(categoryName)) {
      setCategoryNameError(true)
      return
    }
    setCategoryNameError(false)

    createNewCategory({
      categoryName: categoryName.trim(),
      companyId: companyId,
      createAllCompanies: false,
      level: level,
      parent: parentId === 0 ? null : parentId,
      type: level === 1 ? 'Cat' : 'Sub',
    })

    setShowCategoryInputDialog(false)
    setCategoryName('')
    setParentId(0)
    setLevel(1)
  }

  function handleCopyMoveFiles(args) {
    args.cancel = true
    const { itemData, targetData, isCopy } = args

    if (
      !checkPermission({
        action: 'copy',
        type: itemData[0].isFile ? 'file' : 'folder',
        str: itemData[0].filterPath
      })
    ) {
      return
    }

    const targetLevel = checkDepth({ str: targetData.filterPath }) - 1

    const files = itemData.map((f) => {
      const level = checkDepth({ str: f.filterPath }) - 1
      return { ...f, level }
    })

    if (isCopy) {
      copyFiles({
        companyId: companyId,
        files: files,
        target: { ...targetData, level: targetLevel },
      })
    } else {
      moveFiles({
        companyId: companyId,
        files: files,
        target: { ...targetData, level: targetLevel },
      })
    }
  }

  function handleSubmitDeleteFile() {
    deleteFiles({
      companyId: companyId,
      files: filesToDelete,
    })

    setShowDeleteDialog(false)
    setFilesToDelete([])
  }

  function handleDownloadFile(args) {
    const { data } = args

    if (
      !checkPermission({
        action: 'download',
        type: data.data[0].isFile ? 'file' : 'folder',
        str: data.data[0].filterPath
      })
    ) {
      return
    }

    downloadFile({
      file: data.data[0],
      companyId: companyId
    })
  }

  function AddFileButton() {
    return (
      <div className='e-add-file-container'>
        <img
          src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_FILEMANAGER_TOOLBAR_ADDFILE}`}
          alt=''
        />
        <button ref={addFileBtn} className='e-add-file' onClick={() => addNewFile()}>
          {t('fileManager.toolbar.addFile')}
        </button>
      </div>
    )
  }

  function analyzeFileButton() {
    return (
      <div className='e-analyze-file-container'>
        <img
          src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_FILEMANAGER_TOOLBAR_ANALYZE}`}
          alt=''
        />
        <button ref={analyzeFileBtn} className='e-add-file' onClick={() => handleShowModalEntities()}>
          {t('fileManager.contextMenu.analyze')}
        </button>
      </div>
    )
  }

  return (
    <div className='control-section' id='DigitalVault_filemanager_container'>
      <FileManagerComponent
        beforeDownload={(args) => {
          args.cancel = true
          handleDownloadFile(args)
        }}
        beforeMove={(args) => handleCopyMoveFiles(args)}
        created={onCreated}
        id='DigitalVault_filemanager'
        showThumbnail={false}
        fileSystemData={fileSystemData}
        fileLoad={onFileLoad}
        fileOpen={onFileOpen}
        fileSelect={handleFileSelectInternal}
        locale={language}
        menuClick={menuClick}
        path={fileManagerPath}
        ref={fileObj}
        toolbarSettings={{
          items: [
            'NewFolder',
            'Move',
            'Copy',
            'Paste',
            'Delete',
            'Rename',
            'SortBy',
            'Refresh',
            'Selection',
            'View',
            'Details',
          ],
        }}
        contextMenuSettings={{
          file: [
            'Open',
            '|',
            'Copy',
            'Cut',
            '|',
            'Delete',
            'Download',
            'Rename',
            '|',
            'Analyze',
            'Details'
          ],
          folder: [
            'Open',
            '|',
            'Copy',
            'Paste',
            'Cut',
            '|',
            'Delete',
            'Download',
            'Rename',
            '|',
            'Details',
          ],
          layout: [
            'SortBy',
            'View',
            'Refresh',
            '|',
            'NewFolder',
            '|',
            'Details',
            '|',
            'SelectAll',
          ],
        }}
        detailsViewSettings={{
          columns: [
            {
              field: 'name',
              headerText: t('fileManager.listView.header.name'),
              width: 250,
            },
            {
              field: 'type',
              headerText: t('fileManager.listView.header.type'),
              width: 120,
            },
            {
              field: 'dateModified',
              headerText: t('fileManager.listView.header.startDate'),
              sortComparer: sortComparer,
              width: 180,
            },
            {
              field: 'size',
              headerText: t('fileManager.listView.header.size'),
              width: 120,
            },
            {
              field: 'expirationDate',
              headerText: t('fileManager.listView.header.expirationDate'),
              width: 180,
            },
            {
              field: 'statusIA',
              headerText: t('fileManager.listView.header.status.ia'),
              width: 180,
            },
          ],
        }}
        toolbarClick={toolbarClickHandler}
        menuOpen={onMenuOpen}
        view={'Details'}
      >
        <Inject services={[NavigationPane, DetailsView, Toolbar]} />
        <ToolbarItemsDirective>
          <ToolbarItemDirective
            name='NewFolder'
            text={strBtn}
            tooltipText={strBtn}
          />
          <ToolbarItemDirective 
            name='AddFile' 
            template={AddFileButton} 
            visible={showAddFileBtn} 
            showAlwaysInPopup={isMobile}/>
          <ToolbarItemDirective name='SortBy' />
          <ToolbarItemDirective name='Refresh' />
          <ToolbarItemDirective name='Copy' />
          <ToolbarItemDirective name='Paste' />
          <ToolbarItemDirective name='Delete' />
          <ToolbarItemDirective name='Rename' />
          <ToolbarItemDirective name='Selection' />
          <ToolbarItemDirective name='View' />
          <ToolbarItemDirective name='Details' />
          <ToolbarItemDirective name='Download' />
          {showAnalyzeBtn && showAnalyzeFileButton && (
            <ToolbarItemDirective 
              name='AnalyzeFile' 
              template={analyzeFileButton} 
              showAlwaysInPopup={isMobile} />
          )}
        </ToolbarItemsDirective>
      </FileManagerComponent>
      {showPopup && (
        <DropDownBtnHLPure
          optionsDropDown={companies}
          optionsValue={companyId}
          valueItem={'value'}
          nameOption={'name'}
          onDropDownChange={(e) => {
            if (e.target.value === companyId) return
            setCompanyId(e.target.value)
            setIsLoading(true)
            setFirstLoad(true)
          }}
          open={true}
          id={'filemanager-companies'}
          alwaysOpen={true}
        />
      )}
      {showCategoryInputDialog && (
        <DialogYesNoHLPure
          btnCancel={true}
          grid={true}
          cancel={() => setShowCategoryInputDialog(false)}
          handleCancel={() => setShowCategoryInputDialog(false)}
          handleSubmit={strBtn === t('fileManager.toolbar.newCategory')
            ? handleSubmitCategoryInput
            : handleCreateNewSubcategory
          }
          saveBtn={getCategoryDialogText('saveBtn')}
          textBtnCancel={getCategoryDialogText('cancelBtn')}
          titleDialog={getCategoryDialogText('header')}
        >
          <InputHLPure
            inputActive={true}
            errorInput={categoryNameError}
            errorInputText={getCategoryDialogText('errorInputText')}
            labelInput={getCategoryDialogText('categoryInput')}
            handleChangeInput={(e) => setCategoryName(e.target.value)}
            required
            typeInput={'text'}
            valueInput={categoryName}
          />
        </DialogYesNoHLPure>
      )}
      {showCategoryDialog && (
        <DialogTwoActions
          dialogTitle={getCategoryDialogText('confirmHeader')}
          dialogDescription={getCategoryDialogText('confirmDescription')}
          primaryAction={() =>
            handleCreateNewCategory({
              allCompanies: false,
            })
          }
          primaryBtnText={t('fileManager.createCategory.dialog.primaryBtn')}
          secondaryAction={() =>
            handleCreateNewCategory({
              allCompanies: true,
            })
          }
          secondaryBtnText={t('fileManager.createCategory.dialog.secondaryBtn')}
          setCloseDialog={() => setShowCategoryDialog(false)}
        />
      )}
      {showDetailsDialog && (
        <DialogDetailsPure
          children={childrenDialog}
          dialogTitle={t('fileManager.detailsDialog.labels.detailsTitle')}
          showBtnSecondary={true}
          setCloseDialog={() => setShowDetailsDialog(false)}
        />
      )}
      {showRenameDialog && (
        <DialogYesNoHLPure
          btnCancel={true}
          grid={true}
          cancel={() => {
            setShowRenameDialog(false)
            setCategoryNameError(false)
          }}
          handleCancel={() => {
            setShowRenameDialog(false)
            setCategoryNameError(false)
          }}
          handleSubmit={() => handleSubmitRenameInput()}
          saveBtn={t('fileManager.renameFile.dialog.saveBtn')}
          textBtnCancel={t('fileManager.renameFile.dialog.cancel')}
          titleDialog={
            t('fileManager.renameFile.dialog.heading') + ' ' + fileType
          }
        >
          <div style={{ 'marginBottom': '16px' }}>
            <InputHLPure
              inputActive={true}
              errorInput={categoryNameError}
              errorInputText={t('fileManager.renameFile.dialog.errorName')}
              labelInput={
                t('fileManager.renameFile.dialog.label') + ' ' + fileType
              }
              handleChangeInput={(e) => setNewFileName(e.target.value)}
              required
              typeInput={'text'}
              valueInput={newFileName}
            />
          </div>
          {fileType === t('fileManager.renameFile.dialog.cat') && (
            <DropDownBtnHLPure
              labelDropDown={t("digitalVaultCategoriesAdministration.add.minimumVisualization")}
              optionsDropDown={userLevelOptions}
              optionsValue={levelUser}
              valueItem={'levelUser'}
              nameOption={'name'}
              onDropDownChange={(e) => setLevelUser(e.target.value)}
              position='top'
              id={'edit-category'}
            />
          )}
        </DialogYesNoHLPure>
      )}
      {showDeleteDialog && (
        <DialogYesNoHLPure
          btnCancel={true}
          grid={true}
          cancel={() => setShowDeleteDialog(false)}
          handleCancel={() => setShowDeleteDialog(false)}
          handleSubmit={() => handleSubmitDeleteFile()}
          saveBtn={t('fileManager.deleteFile.dialog.saveBtn')}
          textBtnCancel={t('fileManager.deleteFile.dialog.cancel')}
          titleDialog={t('fileManager.deleteFile.dialog.heading')}
        >
          <div className='delete-confirm-container'>
            <p>{t('fileManager.deleteFile.dialog.question')}</p>
            <ul>{filesToDelete.map((file, index) => (
              <li key={index}>
                {file.name}
              </li>
            ))}
            </ul>
          </div>
        </DialogYesNoHLPure>
      )}
      {
        showModalEntities && (
          <DialogExtractEntities
            documentsToOCR={documentsToOCR}
            totalCredits={totalCredits}
            onClickCheckBox={onClickCheckBox}
            closeModalEntities={closeModalEntities}
            saveEntities={saveExtractedEntities}
            onClickSkipBtn={closeModalEntities}
          />
        )
      }
    </div>
  )
}

export default SyncfusionFileManager
